/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable, InjectionToken } from '@angular/core';
import { of$ } from '@gh/rx';
import { map$, filter$, first$ } from '@gh/rx/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleApiLoader } from './google-api-loader.service';
import { GOOGLE_UX_MODE } from '@gh/config';

enum GoogleAuthStatus { Unknown, Initializing, Initialized, Error}

export type ExternalGoogleConfig = google.accounts.oauth2.TokenClientConfig;
export type GoogleTokenResponse = google.accounts.oauth2.TokenResponse;
export type GoogleTokenClient = google.accounts.oauth2.TokenClient;

export const GOOGLE_CONFIG = new InjectionToken<ExternalGoogleConfig>('ExternalGoogleConfig');

@Injectable()
export class GoogleAuthService {
  private status$$ = new BehaviorSubject(GoogleAuthStatus.Unknown);
  private tokenClient: GoogleTokenClient;
  private pickerInited: boolean;
  constructor(private loader: GoogleApiLoader,
              @Inject(GOOGLE_CONFIG) private googleConfig: ExternalGoogleConfig) {
    if (!this.tokenClient && !this.getTokenFromStorage()) {
      this.startInit();
    } else {
      this.status$$.next(GoogleAuthStatus.Initialized);
    }
  }

  get accessToken(): string {
    return this.getTokenFromStorage();
  }

  onPickerLibLoaded(): void {
    this.pickerInited = true;
  }

  signIn(callBack?: (res?: unknown) => void): void {
    if (this.getTokenFromStorage()) {
      if (callBack) {
        callBack();
      }
      return;
    }
    if (!this.tokenClient) {
      this.status$$.next(GoogleAuthStatus.Unknown);
      this.startInit();
      this.waitForTokenClient(() => this.signIn(callBack));
      return;
    }
    this.requestAccessTokenSilentFirst(callBack);
  }

  /**
   * Picker must use a token issued with prompt=consent and trigger_onepick=true.
   * Silent tokens and older session tokens are not valid for Picker after 16 Nov 2026.
   */
  signInForPicker(callBack?: (res?: unknown) => void): void {
    if (this.getPickerTokenFromStorage()) {
      if (callBack) {
        callBack();
      }
      return;
    }
    if (!this.tokenClient) {
      this.status$$.next(GoogleAuthStatus.Unknown);
      this.startInit();
      this.waitForTokenClient(() => this.signInForPicker(callBack));
      return;
    }
    this.requestPickerAccessToken(callBack);
  }

  private requestAccessTokenSilentFirst(userCallback?: (res?: unknown) => void): void {
    const client = this.tokenClient as GoogleTokenClient & { callback: (res: GoogleTokenResponse) => void };
    const requestInteractive = () => {
      client.callback = (res: GoogleTokenResponse) => {        
        this.handleTokenClientResponse(res, userCallback);
      };
      client.requestAccessToken(<any>{ prompt: 'consent', trigger_onepick: true });
    };

    client.callback = (res: GoogleTokenResponse) => {
      if (res.error !== undefined || !res.access_token) {
        requestInteractive();
        return;
      }
      this.handleTokenClientResponse(res, userCallback);
    };
    client.requestAccessToken(<any>{ prompt: '' });
  }

  private requestPickerAccessToken(userCallback?: (res?: unknown) => void): void {
    const client = this.tokenClient as GoogleTokenClient & { callback: (res: GoogleTokenResponse) => void };
    client.callback = (res: GoogleTokenResponse) => {
      this.handleTokenClientResponse(res, userCallback, true);
    };
    client.requestAccessToken(<any>{ prompt: 'consent', trigger_onepick: true });
  }

  private handleTokenClientResponse(
    response: GoogleTokenResponse,
    userCallback?: (res?: unknown) => void,
    pickerAuthorized: boolean = false,
  ): void {
    if (response.error !== undefined || !response.access_token) {
      this.status$$.next(GoogleAuthStatus.Error);
      return;
    }
    this.persistToken(response, pickerAuthorized);
    if (userCallback) {
      userCallback(response);
    }
  }

  private persistToken(response: GoogleTokenResponse, pickerAuthorized: boolean = false): void {
    const expiresInSec = Number(response.expires_in);
    const ttlMs = (Number.isFinite(expiresInSec) && expiresInSec > 0 ? expiresInSec : 3600) * 1000;
    sessionStorage.setItem('googleAuth', JSON.stringify({
      accessToken: response.access_token,
      expireDate: Date.now() + ttlMs,
      // Only a consent + trigger_onepick token may open Picker. Do not copy the flag onto a silent token.
      pickerAuthorized: pickerAuthorized,
    }));
    this.status$$.next(GoogleAuthStatus.Initialized);
  }

  private waitForTokenClient(done: () => void, attemptsLeft: number = 40): void {
    if (this.tokenClient) {
      done();
      return;
    }
    if (attemptsLeft <= 0) {
      return;
    }
    setTimeout(() => this.waitForTokenClient(done, attemptsLeft - 1), 50);
  }

  signOut(): void {
    if (this.status$$.value === GoogleAuthStatus.Unknown) {
      return;
    }
  }

  init(): Observable<boolean> {
    switch (this.status$$.value) {
      case GoogleAuthStatus.Initialized:
        return of$(true);
      case GoogleAuthStatus.Initializing:
        return this.status$$.pipe(filter$((status) => status === GoogleAuthStatus.Initialized), map$(() => true), first$());
      default:
        this.signIn();
        return this.status$$.pipe(filter$((status) => status === GoogleAuthStatus.Initialized), map$(() => true), first$());
    }
  }

  grantOfflineAccess(): Observable<boolean> {
    this.signIn();
    return this.init();
  }

  private startInit(): void {
    const { status$$ } = this;
    const config = {
      ...this.googleConfig,
      include_granted_scopes: this.googleConfig.include_granted_scopes ?? true,
      ux_mode: GOOGLE_UX_MODE,
      redirect_uri: window.location.origin,
      callback: (_res: GoogleTokenResponse) => undefined,
    } as ExternalGoogleConfig;
    if (!window.google || !window.google.accounts) {
      setTimeout(() => this.startInit(), 1000);
    } else {
      this.tokenClient = window.google.accounts.oauth2.initTokenClient(config);
      status$$.next(GoogleAuthStatus.Initializing);
    }
    // client.requestAccessToken();

    // this.loader.load('auth2').subscribe(
    //   () => {
    //     const auth = gapi.auth2.init(this.googleConfig);
    //
    //     auth.then(
    //       () => status$$.next(GoogleAuthStatus.Initialized),
    //       () => status$$.next(GoogleAuthStatus.Error));
    //   },
    //   () => status$$.next(GoogleAuthStatus.Error));
  }

  private getTokenFromStorage(): string {
    const stored = this.readStoredAuth();
    return stored?.accessToken || '';
  }

  private getPickerTokenFromStorage(): string {
    const stored = this.readStoredAuth();
    return stored?.pickerAuthorized ? stored.accessToken : '';
  }

  private readStoredAuth(): Maybe<{ accessToken: string; expireDate: number; pickerAuthorized?: boolean }> {
    const googleAuth = sessionStorage.getItem('googleAuth');
    if (!googleAuth) {
      return void 0;
    }
    const tokenJson = JSON.parse(googleAuth);
    const accessToken = tokenJson.accessToken;
    const expire = tokenJson.expireDate;
    if (!accessToken || !(expire > new Date().getTime())) {
      return void 0;
    }
    return {
      accessToken,
      expireDate: expire,
      pickerAuthorized: Boolean(tokenJson.pickerAuthorized),
    };
  }
}
